import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { withProcedureWraper } from "../../../..";
import moment from "moment";
import { Col, Form, Row, Spin, notification } from "antd";
import { useQueryClient } from "@tanstack/react-query";

import {
  AlternateApproverSelect,
  ApproverSelect,
  ButtonList,
  Icon,
} from "../../../../../../components";
import { useMTPRequests } from "../../../monthly-training-plan/hooks";
import { Routes } from "../../../../../../AppRoutes";
import { useCreateMTPS, useMTPSSteps } from "../../hooks";
import {
  DepartmentCode,
  EnvKey,
  RequestStatus,
  Rules,
  SystemCode,
} from "../../../../../../constants";
import { EnvHelper, StringHelper } from "../../../../../../helper";
import { IAPList, useEmployee } from "../../../../../../hook";
import { MTPDetailTable } from "../../components";

type MTPSForm = {
  eApprovedBy: string;
  eAlternateApprover: string;
  eITLead: string;
  eAlternateITLead: string;
  eAssignee: string;
  eAlternateAssignee: string;
};

const siteUrl = EnvHelper.getValue(EnvKey.APPROVER_MTPS_URL);

const CreateMTPSPage = withProcedureWraper(() => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<MTPSForm>();
  const history = useHistory();
  const location = useLocation();

  const [eApprovedBy, setEApprovedBy] = useState("");
  const [eITLead, setEITLead] = useState("");
  const [eAssignee, setEAssignee] = useState("");

  const { getAvailableStep, sendMailAP } = useMTPSSteps();
  const { getEmployees } = useEmployee();
  const params = new URLSearchParams(location.search);
  const defaultMonth = params.get("month");
  const defaultDepartment = params.get("department");

  const { data, isLoading: isMTPRequestsLoading } = useMTPRequests();

  const { mutate, isLoading: isCreateLoading } = useCreateMTPS();

  const filteredRequests: any[] = useMemo(() => {
    if (!defaultMonth || !defaultDepartment || !data) return [];
    const filteredRequests = data.filter((item: any) => {
      const { eMonth, eRequestStatus } = item;
      const isApprovedRequest = eRequestStatus === RequestStatus.completed;
      const isSameMonth = moment(defaultMonth).isSame(eMonth, "month");
      return isApprovedRequest && isSameMonth;
    });
    return filteredRequests.filter(({ eDepartment }: any) => {
      const depCode = StringHelper.verticalBarSplit(eDepartment)[0];
      if (defaultDepartment === "1" && !["2", "3"].includes(depCode)) {
        return true;
      }
      if (defaultDepartment === depCode) return true;
      return false;
    });
  }, [data, defaultMonth, defaultDepartment]);

  const handleValuesChange = (changedValue: Partial<MTPSForm> = {}) => {
    const {
      eApprovedBy,
      eAlternateApprover,
      eITLead,
      eAlternateITLead,
      eAssignee,
      eAlternateAssignee,
    } = changedValue;

    if (eApprovedBy || eAlternateApprover) {
      const value = eApprovedBy || eAlternateApprover;
      setEApprovedBy(value!);
    }
    if (eITLead || eAlternateITLead) {
      const value = eITLead || eAlternateITLead;
      setEITLead(value!);
    }
    if (eAssignee || eAlternateAssignee) {
      const value = eAssignee || eAlternateAssignee;
      setEAssignee(value!);
    }
  };

  const dataByDepartment = useMemo(() => {
    if (defaultDepartment === DepartmentCode.TSN) {
      return {
        eLocations: ["TSN", "VTSN"],
        leadTitle: "Trưởng phòng TCNL",
        assigneeTitle: "Tổng Giám đốc",
      };
    }
    if (defaultDepartment === DepartmentCode.NB) {
      return {
        eLocations: ["NBA", "VNBA"],
        leadTitle: "Trưởng phòng KHHC",
        assigneeTitle: "Giám đốc chi nhánh",
      };
    }
    if (defaultDepartment === DepartmentCode.DN) {
      return {
        eLocations: ["DAD", "VDAD"],
        leadTitle: "Trưởng phòng KHHC",
        assigneeTitle: "Giám đốc chi nhánh",
      };
    }
    return { eLocations: [], leadTitle: "", assigneeTitle: "" };
  }, [defaultDepartment]);

  const handleFinish = () => {
    const step = getAvailableStep(SystemCode.MTPS);
    const eApprovalProcess: IAPList = [
      [step.initialize],
      [
        {
          ...step.empty,
          title: "Trung tâm đào tạo",
          name: StringHelper.verticalBarSplit(eApprovedBy)[1],
          email: StringHelper.verticalBarSplit(eApprovedBy)[0],
          requestStatus: RequestStatus.inProgress,
        },
      ],
      [
        {
          ...step.initialize,
          title: "Bộ phận TCNL",
          date: "",
          status: "",
          requestStatus: RequestStatus.inProgress,
        },
      ],
      [
        {
          ...step.empty,
          title: "Trung tâm đào tạo",
          name: StringHelper.verticalBarSplit(eApprovedBy)[1],
          email: StringHelper.verticalBarSplit(eApprovedBy)[0],
          requestStatus: RequestStatus.inProgress,
        },
      ],
      [
        {
          ...step.eApprovedByITLead,
          title: "Trưởng trung tâm đào tạo",
          requestStatus: RequestStatus.inProgress,
        },
      ],
      [
        {
          ...step.eApprovedByITHead,
          title: dataByDepartment.leadTitle,
          name: StringHelper.verticalBarSplit(eITLead)[1],
          email: StringHelper.verticalBarSplit(eITLead)[0],
          requestStatus: RequestStatus.inProgress,
        },
      ],
      [
        {
          ...step.eAssignee,
          title: dataByDepartment.assigneeTitle,
          name: StringHelper.verticalBarSplit(eAssignee)[1],
          email: StringHelper.verticalBarSplit(eAssignee)[0],
          requestStatus: RequestStatus.completed,
        },
      ],
    ];
    const body = {
      eContent: JSON.stringify(filteredRequests),
      eApprovedBy,
      eDepartmentCode: defaultDepartment,
      eApprovalProcess,
    };

    mutate(body, {
      onSuccess(response: any) {
        queryClient.invalidateQueries({ queryKey: ["MTPS-requests"] });
        notification.success({
          message: "Thành công!",
          description: `Tạo yêu cầu thành công.`,
        });
        sendMailAP(response.fields.eApprovalProcess, {
          toPath: `${siteUrl}/${response.id}`,
          type: RequestStatus.approved,
        });
        history.push(`${Routes.mtpsPage.path}/${response.id}`);
      },
      onError() {
        notification.error({
          message: "Thất bại",
          description: `Tạo yêu cầu thất bại.`,
        });
      },
    });
  };

  const eApproverByOptions = useMemo(() => {
    return getEmployees({ eDepCodes: [DepartmentCode.TC] });
  }, [getEmployees]);
  const eITLeadOptions = useMemo(() => {
    return getEmployees({
      eBizTitleCodes: ["PP", "TP"],
      eLocations: dataByDepartment.eLocations,
    });
  }, [getEmployees, dataByDepartment.eLocations]);
  const eAssigneeOptions = useMemo(() => {
    return getEmployees({
      eBizTitleCodes: ["GD", "PGĐ"],
      eLocations: dataByDepartment.eLocations,
    });
  }, [getEmployees, dataByDepartment.eLocations]);

  const isLoading = isMTPRequestsLoading || isCreateLoading;

  return (
    <Spin spinning={isLoading}>
      <Form
        form={form}
        layout="vertical"
        size="large"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <div
          style={{ height: "calc(100vh - 60px)" }}
          className="pd15 flex flex-column"
        >
          <div className="PageHead flex-center">
            <div className="title" style={{ margin: 0 }}>
              <div className="btnBack" onClick={() => history.goBack()}>
                <Icon.ArrowLeft />
              </div>
              Tạo bảng tổng hợp kế hoạch đào tạo tháng
            </div>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <MTPDetailTable dataSource={filteredRequests} />
          </div>

          <Row gutter={[32, 0]}>
            <Col span={12} xs={24} md={12}>
              <Form.Item
                required
                name="eApprovedBy"
                label="Người duyệt (Trung tâm đào tạo)"
                rules={[Rules.required2Field("eAlternateApprover")]}
              >
                <ApproverSelect
                  placeholder="Chọn người duyệt..."
                  options={eApproverByOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item
                label="Người duyệt thay thế (Trung tâm đào tạo)"
                name="eAlternateApprover"
              >
                <AlternateApproverSelect placeholder="Chọn người duyệt thay thế..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[32, 0]}>
            <Col span={12} xs={24} md={12}>
              <Form.Item
                required
                label={`Người duyệt (${dataByDepartment.leadTitle})`}
                name="eITLead"
                rules={[Rules.required2Field("eAlternateITLead")]}
              >
                <ApproverSelect
                  placeholder="Chọn người duyệt..."
                  options={eITLeadOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item
                label={`Người duyệt thay thế (${dataByDepartment.leadTitle})`}
                name="eAlternateITLead"
              >
                <AlternateApproverSelect placeholder="Chọn người duyệt thay thế..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[32, 0]}>
            <Col span={12} xs={24} md={12}>
              <Form.Item
                required
                label={`Người duyệt (${dataByDepartment.assigneeTitle})`}
                name="eAssignee"
                rules={[Rules.required2Field("eAlternateAssignee")]}
              >
                <ApproverSelect
                  placeholder="Chọn người duyệt..."
                  options={eAssigneeOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item
                label={`Người duyệt thay thế (${dataByDepartment.assigneeTitle})`}
                name="eAlternateAssignee"
              >
                <AlternateApproverSelect placeholder="Chọn người duyệt thay thế..." />
              </Form.Item>
            </Col>
          </Row>
          <div className="flex flex-end">
            <ButtonList
              items={[
                {
                  type: "submit",
                  buttonType: "warning",
                  label: "Xác nhận",
                },
              ]}
            />
          </div>
        </div>
      </Form>
    </Spin>
  );
});

export default CreateMTPSPage;
